import React, { useState, useEffect } from "react";

import { Shield, User, Key, LogOut, AlertTriangle } from "lucide-react";

import Button from "../../Components/Button";
import Input from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  changeMyPassword,
  clearChangeMyPasswordState,
} from "../../Store/Reducers/AdminSlice";
import { fetchPublicKey } from "../../Store/Reducers/AuthSlice";
import {
  ROLES,
  ROLE_DISPLAY_NAMES,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import { encryptWithPublicKey } from "../../Utils/Helper/clientEncryption";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

interface AdminProfileSettingsModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSignOut?: () => void;
}

const AdminProfileSettingsModal: React.FC<AdminProfileSettingsModalProps> = ({
  isOpen,
  toggle,
  onSignOut,
}) => {
  const dispatch = useAppDispatch();
  const { changeMyPasswordLoading, changeMyPasswordSuccess } = useAppSelector(
    (state: any) => state.admin
  );
  const { publicKey } = useAppSelector((state: any) => state.authentication);

  const [activeTab, setActiveTab] = useState<
    "profile" | "password" | "account"
  >("profile");

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Get user data from localStorage
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userEmail = loggedInUser?.email || "";
  const userName = loggedInUser?.fullName || "ExecBridge Admin";
  const userRole = loggedInUser?.role || "";
  const normalizedRole = normalizeRoleName(userRole);
  const roleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "Super Administrator";

  const isSuperAdmin = normalizedRole === ROLES.IZBA_ADMIN;

  const tabs = [
    {
      id: "profile",
      label: "Profile",
      icon: <User size={18} />,
    },
    {
      id: "password",
      label: "Password",
      icon: <Key size={18} />,
    },
    {
      id: "account",
      label: "Account",
      icon: <Shield size={18} />,
    },
  ];

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (passwordErrors[name as keyof typeof passwordErrors]) {
      setPasswordErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };
    let isValid = true;

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
      isValid = false;
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
      isValid = false;
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
      isValid = false;
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
      isValid = false;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setPasswordErrors(errors);
    return isValid;
  };

  const handleUpdatePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    let key = publicKey;
    if (!key) {
      const response = await dispatch(fetchPublicKey());
      if (response.meta.requestStatus === "fulfilled") {
        key = (response.payload as string) || null;
      }
    }

    if (!key) {
      return;
    }

    let encryptedCurrentPassword = "";
    let encryptedNewPassword = "";
    try {
      [encryptedCurrentPassword, encryptedNewPassword] = await Promise.all([
        encryptWithPublicKey(key, passwordData.currentPassword),
        encryptWithPublicKey(key, passwordData.newPassword),
      ]);
    } catch (encryptionError) {
      return;
    }

    dispatch(
      changeMyPassword({
        current_password: encryptedCurrentPassword,
        new_password: encryptedNewPassword,
      })
    );
  };

  const handleSignOut = () => {
    toggle(); // Close the profile settings modal first
    if (onSignOut) {
      onSignOut(); // Trigger the confirmation modal
    }
  };

  // Clear password form and close modal when password is successfully updated
  useEffect(() => {
    if (changeMyPasswordSuccess) {
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      dispatch(clearChangeMyPasswordState());
      toggle(); // Close the modal
    }
  }, [changeMyPasswordSuccess, dispatch, toggle]);

  // Clear password state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      dispatch(clearChangeMyPasswordState());
    }
  }, [isOpen, dispatch]);

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title="Admin Profile Settings"
      size="lg"
      className="admin-profile-modal"
    >
      <div className="flex flex-col">
        <div className="mb-4 border-b border-gray-200">
          <TabsComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={tabId => setActiveTab(tabId as typeof activeTab)}
            variant="tab-underline"
          />
        </div>

        <div className="min-h-[300px]">
          {activeTab === "profile" && (
            <div className="flex flex-col items-center py-8">
              <div className="mb-6 relative">
                <div className="w-[120px] h-[120px] rounded-full bg-gradient-to-br from-[#974ae4] to-[#7c3aed] flex items-center justify-center mx-auto shadow-lg ring-4 ring-purple-100">
                  <Shield size={40} className="text-white" strokeWidth={2} />
                </div>
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{userName}</h3>
                <p className="text-sm text-gray-500 font-medium">{userEmail}</p>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 mt-3 bg-purple-50 rounded-full border border-purple-100">
                  <Shield
                    size={14}
                    className="text-[#974ae4]"
                    strokeWidth={2.5}
                  />
                  <p className="text-sm text-[#974ae4] font-medium">
                    {roleDisplayName}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "password" && (
            <div className="pb-2">
              <div className="flex flex-col gap-5 max-w-[520px] mx-auto">
                <div className="mb-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    Change Password
                  </h3>
                  <p className="text-sm text-gray-500">
                    Update your password to keep your account secure
                  </p>
                </div>
                <Input
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors.currentPassword}
                  required
                />
                <Input
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors.newPassword}
                  required
                />
                <Input
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors.confirmPassword}
                  required
                />
                <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                  <Button
                    variant="primary"
                    onClick={handleUpdatePassword}
                    loading={changeMyPasswordLoading}
                    icon={
                      <Key size={16} className="text-white" strokeWidth={2.5} />
                    }
                    className="min-w-[160px]"
                  >
                    Update Password
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "account" && (
            <div className="pb-2">
              <div className="max-w-[600px] mx-auto">
                {isSuperAdmin && (
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl mb-6">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle
                          size={18}
                          className="text-red-600"
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-red-700 mb-1">
                        Super Administrator Account
                      </h4>
                      <p className="text-sm text-red-600 leading-relaxed">
                        This account has full system access and cannot be
                        deleted.
                      </p>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    Account Information
                  </h3>
                  <p className="text-sm text-gray-500">
                    View your account details and settings
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <Shield
                          size={16}
                          className="text-gray-400"
                          strokeWidth={2}
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          Account Type
                        </span>
                      </div>
                      <span className="text-sm text-[#974ae4] font-medium px-3 py-1 bg-purple-50 rounded-md">
                        {roleDisplayName}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        <span className="text-sm text-gray-700 font-medium">
                          Multi-Tenant Access
                        </span>
                      </div>
                      <span className="text-sm text-green-600 font-medium px-3 py-1 bg-green-50 rounded-md">
                        Enabled
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        <span className="text-sm text-gray-700 font-medium">
                          Organization Management
                        </span>
                      </div>
                      <span className="text-sm text-green-600 font-medium px-3 py-1 bg-green-50 rounded-md">
                        Full Access
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <Button
                    variant="danger"
                    onClick={handleSignOut}
                    icon={
                      <LogOut
                        size={16}
                        className="text-white"
                        strokeWidth={2.5}
                      />
                    }
                    className="min-w-[140px]"
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default AdminProfileSettingsModal;
